import {
  IconAlertTriangle,
  IconBulb,
  IconFlask,
  IconPill,
  IconQuestionMark,
  IconScale,
  IconStethoscope,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import type { SupportedLanguage } from "../../../../../api/src/shared/domain/language";

// The look of each component of a composed answer (`genui-catalog.ts`). The
// conversation and the genUI playground render the same components: each one
// takes the texts and the source lines that its caller has already rendered.
// A text can hold a block element, so its container is a `div`, never a `p`.

// The components' own labels follow the language of the answer, as the answer's panels do.
function useLayoutLabels(language: SupportedLanguage) {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(language);
  return {
    keyPoints: t("Key points"),
    missing: t("Missing information"),
    recommendation: t("Recommendation"),
    reportedBy: t("Reported by"),
    likely: t("Most likely"),
    dontMiss: t("Do not miss"),
    redFlags: t("Red flags:"),
    other: t("Other causes"),
    firstLine: t("First line"),
    secondLine: t("Second line"),
    population: t("Population"),
    endpoint: t("Primary endpoint"),
    result: t("Result"),
    indication: t("Indication"),
    dose: t("Dose"),
    renal: t("Renal function"),
    interactions: t("Interactions"),
    notStated: t("Not stated in the sources"),
    debated: t("Still debated"),
    researchQuestion: t("Research question"),
    comparison: t("Comparison"),
    outcome: t("Outcome"),
    method: t("Method"),
  };
}
type LayoutLabels = ReturnType<typeof useLayoutLabels>;

const KEY_POINT_TONES = {
  finding: {
    panel: "bg-ds-blueberry-50 text-ds-blueberry-900",
    Icon: IconBulb,
  },
  caution: {
    panel:
      "bg-ds-clementine-50 text-ds-clementine-700 border border-ds-black/6",
    Icon: IconAlertTriangle,
  },
  gap: { panel: "bg-ds-slate-100 text-ds-slate-700", Icon: IconQuestionMark },
} as const satisfies Record<
  "finding" | "caution" | "gap",
  { panel: string; Icon: typeof IconBulb }
>;

// A labelled value of a card: a text, or the label's statement that the sources do not give it.
function Field({
  label,
  text,
  labels,
}: {
  label: string;
  text: ReactNode | null;
  labels: LayoutLabels;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-caption font-semibold text-ds-slate-600">{label}</dt>
      <dd className="m-0 text-footnote text-ds-mint-900">
        {text === null ? (
          <span className="text-ds-slate-500 italic">{labels.notStated}</span>
        ) : (
          text
        )}
      </dd>
    </div>
  );
}

function Overline({
  Icon,
  className,
  children,
}: {
  Icon: typeof IconBulb;
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <p
      className={cn(
        "m-0 flex items-center gap-1.5 text-caption font-semibold uppercase",
        className
      )}
    >
      <Icon stroke={1.5} className="size-4 shrink-0" />
      {children}
    </p>
  );
}

export function LayoutSection({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 text-publication-lg font-bold text-ds-mint-800">
        {title}
      </h3>
      {children}
    </section>
  );
}

// A key points panel, in the look of the Markdown answer's panel (CalloutPanel, key tone).
export function LayoutSummary({
  points,
  language,
}: {
  points: ReactNode[];
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl bg-ds-blueberry-50 p-4 text-footnote">
      <p className="m-0 flex items-center gap-1.5 font-dm-sans text-ui-body font-medium text-ds-blueberry-800">
        <IconBulb stroke={1.5} className="size-5 shrink-0" />
        {labels.keyPoints}
      </p>
      <ul className="m-0 flex list-none flex-col gap-4 p-0 font-dm-sans text-ds-mint-800">
        {points.map((point, index) => (
          <li
            key={index}
            className="relative ps-4 before:absolute before:inset-s-0 before:top-0 before:text-ds-blueberry-800 before:content-['•']"
          >
            {point}
          </li>
        ))}
      </ul>
    </div>
  );
}

// The data of the reader's case that would change the answer, in the look of the Markdown answer's list.
export function LayoutMissing({
  questions,
  language,
}: {
  questions: string[];
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <div className="flex flex-col gap-2 text-gpt-answer text-ds-slate-700">
      <div className="font-semibold text-ds-mint-800">{labels.missing}</div>
      <ul className="m-0 flex list-disc flex-col gap-1 ps-5">
        {questions.map((question, index) => (
          <li key={index}>{question}</li>
        ))}
      </ul>
    </div>
  );
}

export function LayoutParagraph({
  text,
}: {
  text: ReactNode;
}): React.JSX.Element {
  return <div className="text-gpt-answer text-ds-slate-700">{text}</div>;
}

export function LayoutKeyPoint({
  tone,
  text,
}: {
  tone: keyof typeof KEY_POINT_TONES;
  text: ReactNode;
}): React.JSX.Element {
  const { panel, Icon } = KEY_POINT_TONES[tone];
  return (
    <div className={cn("flex gap-2 rounded-2xl p-3", panel)}>
      <Icon className="mt-0.5 size-4 shrink-0" stroke={1.5} />
      <div className="min-w-0 text-ui-body">{text}</div>
    </div>
  );
}

// `source` is the card's source line: its number and the facts of the source.
export function LayoutStudyCard({
  source,
  takeaway,
}: {
  source: ReactNode;
  takeaway: ReactNode;
}): React.JSX.Element {
  return (
    <article className="flex flex-col items-start gap-2 rounded-2xl border border-ds-black/6 bg-white p-4">
      {source}
      <div className="w-full border-t border-ds-black/6 pt-3 text-ui-body text-ds-mint-900">
        {takeaway}
      </div>
    </article>
  );
}

export function LayoutReferenceCard({
  source,
  population,
  endpoint,
  result,
  language,
}: {
  source: ReactNode;
  population: ReactNode | null;
  endpoint: ReactNode | null;
  result: ReactNode;
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <article className="flex flex-col items-start gap-3 rounded-2xl border border-ds-black/6 bg-white p-4">
      {source}
      <dl className="m-0 flex w-full flex-col gap-2 border-t border-ds-black/6 pt-3">
        <Field label={labels.population} text={population} labels={labels} />
        <Field label={labels.endpoint} text={endpoint} labels={labels} />
        <Field label={labels.result} text={result} labels={labels} />
      </dl>
    </article>
  );
}

// One card per row in a narrow column, a table from the `@lg` container width.
// `source` is the row's source name, or null for a row that names none.
export function LayoutEvidenceTable({
  columns,
  rows,
}: {
  columns: ReactNode[];
  rows: { label: ReactNode; source: ReactNode | null; cells: ReactNode[] }[];
}): React.JSX.Element {
  const rowName = (row: (typeof rows)[number]) => (
    <div className="flex flex-col gap-1">
      <div className="font-semibold">{row.label}</div>
      {row.source === null ? null : (
        <div className="text-caption text-ds-slate-600">{row.source}</div>
      )}
    </div>
  );
  return (
    <div className="@container">
      <div className="flex flex-col gap-2 @lg:hidden">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="flex flex-col gap-2 rounded-2xl border border-ds-black/6 bg-white p-3 text-footnote text-ds-mint-900"
          >
            {rowName(row)}
            <dl className="m-0 flex flex-col gap-1.5">
              {row.cells.map((cell, index) => {
                const column = columns[index];
                if (column === undefined)
                  throw new Error(
                    "should not happen: the catalog checks one cell per column"
                  );
                return (
                  <div key={index} className="flex flex-col gap-0.5">
                    <dt className="text-caption font-semibold text-ds-slate-600">
                      {column}
                    </dt>
                    <dd className="m-0">{cell}</dd>
                  </div>
                );
              })}
            </dl>
          </div>
        ))}
      </div>
      <div className="hidden overflow-x-auto rounded-2xl border border-ds-black/6 bg-white @lg:block">
        <table className="w-full border-collapse text-left text-footnote text-ds-mint-900">
          <thead className="bg-ds-slate-100">
            <tr>
              <th className="p-2" />
              {columns.map((column, index) => (
                <th key={index} className="p-2 font-semibold">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-t border-ds-black/6">
                <td className="p-2 align-top">{rowName(row)}</td>
                {row.cells.map((cell, index) => (
                  <td key={index} className="p-2 align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A guideline states its own year beside its issuer; another source only
// reports the recommendation, and its year is not the year of the guideline.
export function LayoutRecommendation({
  issuer,
  text,
  source,
  origin,
  language,
}: {
  issuer: ReactNode;
  text: ReactNode;
  source: ReactNode;
  origin: { kind: "guideline"; year: number | null } | { kind: "reported" };
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <aside className="flex flex-col gap-2 rounded-2xl border border-ds-black/6 border-l-4 border-l-ds-blueberry-600 bg-white p-4">
      <Overline Icon={IconStethoscope} className="text-ds-blueberry-800">
        {labels.recommendation}
      </Overline>
      <div className="text-ui-body font-semibold text-ds-mint-900">
        {issuer}
        {origin.kind === "guideline" && origin.year !== null
          ? ` ${String(origin.year)}`
          : null}
      </div>
      <div className="text-ui-body text-ds-mint-900">{text}</div>
      <div className="flex items-start gap-1.5 text-footnote text-ds-slate-600">
        {origin.kind === "reported" ? (
          <span className="shrink-0">{labels.reportedBy}</span>
        ) : null}
        {source}
      </div>
    </aside>
  );
}

type DifferentialItem = { name: ReactNode; text: ReactNode };

export function LayoutDifferential({
  likely,
  dontMiss,
  other,
  language,
}: {
  likely: DifferentialItem[];
  dontMiss: DifferentialItem[];
  other: DifferentialItem[];
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  const groups = [
    {
      key: "likely",
      title: labels.likely,
      accent: "text-ds-mint-800",
      Icon: IconStethoscope,
      items: likely,
      flags: false,
    },
    {
      key: "dontMiss",
      title: labels.dontMiss,
      accent: "text-ds-strawberry-600",
      Icon: IconAlertTriangle,
      items: dontMiss,
      flags: true,
    },
    {
      key: "other",
      title: labels.other,
      accent: "text-ds-slate-600",
      Icon: IconQuestionMark,
      items: other,
      flags: false,
    },
  ].filter((group) => group.items.length > 0);
  return (
    <div className="flex flex-col divide-y divide-ds-black/6 rounded-2xl border border-ds-black/6 bg-white">
      {groups.map((group) => (
        <section key={group.key} className="flex flex-col gap-2 p-3">
          <Overline Icon={group.Icon} className={group.accent}>
            {group.title}
          </Overline>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {group.items.map((item, index) => (
              <li key={index} className="flex flex-col gap-0.5">
                <div className="text-ui-body font-semibold text-ds-mint-900">
                  {item.name}
                </div>
                <div className="text-footnote text-ds-slate-700">
                  {group.flags ? (
                    <span className="font-semibold text-ds-strawberry-600">{`${labels.redFlags} `}</span>
                  ) : null}
                  {item.text}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function LayoutOrderedSteps({
  steps,
  language,
}: {
  steps: {
    title: ReactNode;
    text: ReactNode;
    line: "first" | "second" | "none";
  }[];
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <ol className="m-0 flex list-none flex-col gap-3 p-0">
      {steps.map((step, index) => (
        <li key={index} className="flex gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ds-blueberry-50 text-caption font-semibold text-ds-blueberry-800">
            {index + 1}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2 text-ui-body font-semibold text-ds-mint-900">
              <span>{step.title}</span>
              {step.line === "none" ? null : (
                <span className="rounded-full bg-ds-mint-50 px-2 py-0.5 text-caption font-semibold text-ds-mint-800">
                  {step.line === "first" ? labels.firstLine : labels.secondLine}
                </span>
              )}
            </div>
            <div className="text-footnote text-ds-slate-700">{step.text}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function LayoutMechanismList({
  items,
}: {
  items: { term: ReactNode; text: ReactNode }[];
}): React.JSX.Element {
  return (
    <dl className="m-0 flex flex-col gap-3">
      {items.map((item, index) => (
        <div key={index} className="flex flex-col gap-0.5">
          <dt className="text-ui-body font-semibold text-ds-mint-900">
            {item.term}
          </dt>
          <dd className="m-0 text-footnote text-ds-slate-700">{item.text}</dd>
        </div>
      ))}
    </dl>
  );
}

// A card without an indication hides the line; a missing dose fact says the sources do not give it.
export function LayoutDrugCard({
  name,
  indication,
  dose,
  renal,
  interactions,
  language,
}: {
  name: ReactNode;
  indication: ReactNode | null;
  dose: ReactNode;
  renal: ReactNode | null;
  interactions: ReactNode | null;
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-ds-black/6 bg-white p-4">
      <div className="flex items-center gap-2 text-heading-xs text-ds-mint-900">
        <IconPill stroke={1.5} className="size-5 shrink-0" />
        {name}
      </div>
      <dl className="m-0 flex flex-col gap-2">
        {indication === null ? null : (
          <Field label={labels.indication} text={indication} labels={labels} />
        )}
        <Field label={labels.dose} text={dose} labels={labels} />
        <Field label={labels.renal} text={renal} labels={labels} />
        <Field
          label={labels.interactions}
          text={interactions}
          labels={labels}
        />
      </dl>
    </article>
  );
}

export function LayoutDebated({
  question,
  positions,
  language,
}: {
  question: ReactNode;
  positions: { label: ReactNode; text: ReactNode }[];
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <section className="flex flex-col gap-3 rounded-2xl bg-ds-slate-100 p-4">
      <Overline Icon={IconScale} className="text-ds-slate-700">
        {labels.debated}
      </Overline>
      <div className="text-ui-body font-semibold text-ds-mint-900">
        {question}
      </div>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {positions.map((position, index) => (
          <li
            key={index}
            className="flex flex-col gap-0.5 rounded-xl bg-white p-3"
          >
            <div className="text-footnote font-semibold text-ds-mint-900">
              {position.label}
            </div>
            <div className="text-footnote text-ds-slate-700">
              {position.text}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function LayoutResearchQuestion({
  title,
  question,
  population,
  comparison,
  outcome,
  method,
  language,
}: {
  title: ReactNode;
  question: ReactNode;
  population: ReactNode;
  comparison: ReactNode;
  outcome: ReactNode;
  method: ReactNode;
  language: SupportedLanguage;
}): React.JSX.Element {
  const labels = useLayoutLabels(language);
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-ds-black/6 bg-white p-4">
      <Overline Icon={IconFlask} className="text-ds-blueberry-800">
        {labels.researchQuestion}
      </Overline>
      <div className="text-heading-xs text-ds-mint-900">{title}</div>
      <div className="text-ui-body text-ds-mint-900">{question}</div>
      <dl className="m-0 flex flex-col gap-2 border-t border-ds-black/6 pt-3">
        <Field label={labels.population} text={population} labels={labels} />
        <Field label={labels.comparison} text={comparison} labels={labels} />
        <Field label={labels.outcome} text={outcome} labels={labels} />
        <Field label={labels.method} text={method} labels={labels} />
      </dl>
    </article>
  );
}
